'use client';

import { memo, useCallback, useMemo, type SyntheticEvent } from 'react';
import { useDraggable, type DraggableAttributes, type DraggableSyntheticListeners } from '@dnd-kit/core';
import { Pencil } from 'lucide-react';
import { AvailabilityStatusIcon } from '@/components/tournament/fixture/AvailabilityStatusIcon';
import type { GridMatch } from '@/types/tournament.model';
import { fitsAvailability } from '@/utils/availability-fit';
import { cn } from '@/lib/utils';
import { GridAvailabilityPopover } from './GridAvailabilityPopover';
import {
  teamLabel,
  TOURNAMENT_DOT_COLORS,
  type PlaceholderLabels,
} from './grid-model';

interface GridMatchCardProps {
  match: GridMatch;
  tournamentName: string;
  colorIndex: number;
  /** The cell where the card currently sits, used to evaluate availability. */
  date: string;
  time: string;
  /** Moved and not yet saved. */
  pending?: boolean;
  /**
   * True while there's an unsaved drag-and-drop draft elsewhere in the grid. The fine-grained
   * modal saves one match at a time straight to the backend; mixing that with a pending batch
   * would leave the batch's own draft silently out of sync with what the backend now has, so
   * editing is blocked until the draft is saved or discarded.
   */
  editDisabled?: boolean;
  /** Qualifier labels for a bracket slot whose team isn't decided yet, e.g. `"2° Grupo B"`. */
  placeholders?: PlaceholderLabels;
  onEdit: (match: GridMatch) => void;
}

interface GridMatchCardViewProps extends GridMatchCardProps {
  setNodeRef?: (node: HTMLElement | null) => void;
  dragListeners?: DraggableSyntheticListeners;
  dragAttributes?: DraggableAttributes;
  isDragging?: boolean;
}

/**
 * Pure presentational card: no drag behavior of its own. `GridMatchCard` below wraps this with
 * `useDraggable` for the real cards in the grid; `DragOverlay` in `SharedGridView` renders this
 * component directly instead.
 *
 * The overlay MUST use this presentational version, not the draggable wrapper: `useDraggable`
 * registers its node in dnd-kit's internal `draggableNodes` map keyed by `id`. Mounting a second
 * `useDraggable` instance with the same id (the overlay preview, while the real card is still
 * mounted underneath) steals that registration. When the overlay unmounts on drop or cancel, its
 * cleanup deletes the entry — leaving that id unregistered, since the real card's own effect
 * never re-runs (its deps didn't change). The next drag attempt on that same match then silently
 * does nothing, because dnd-kit's sensors bail out when the id has no registered node. Don't
 * "simplify" this back into one component.
 *
 * Memoized because every cell subscribes to dnd-kit's drag context (through `useDroppable`), so
 * all of them re-render on every pointer-move tick of a drag. Without this, each tick also
 * re-runs both availability scans for every visible card.
 */
export const GridMatchCardView = memo(function GridMatchCardView({
  match,
  tournamentName,
  colorIndex,
  date,
  time,
  pending = false,
  editDisabled = false,
  placeholders,
  onEdit,
  setNodeRef,
  dragListeners,
  dragAttributes,
  isDragging = false,
}: GridMatchCardViewProps) {
  // React bubbles events up the component tree, not the DOM, so whatever the card renders into a
  // portal (the "Horarios" popover) would reach these listeners too: selecting text in the popover
  // with the mouse, or a press-and-hold on it, started dragging the card behind it. Only events
  // whose DOM target is inside the card itself may start a drag.
  const listeners = useMemo(() => {
    if (!dragListeners) return undefined;
    return Object.fromEntries(
      Object.entries(dragListeners).map(([name, handler]) => [
        name,
        (event: SyntheticEvent) => {
          if (event.currentTarget.contains(event.target as Node)) handler(event);
        },
      ]),
    );
  }, [dragListeners]);

  const team1Ok = fitsAvailability(match.team1Availability, date, time);
  const team2Ok = fitsAvailability(match.team2Availability, date, time);
  const locked = match.hasResult;
  const team1Name = teamLabel(match.team1, 'Equipo 1', placeholders?.team1);
  const team2Name = teamLabel(match.team2, 'Equipo 2', placeholders?.team2);

  // A loaded result takes priority over the "pending draft" reason: it's blocked for good
  // (same rule the drag handle already follows below), not just until the draft clears.
  const editBlockedReason = locked
    ? 'Este partido ya tiene un resultado cargado: no se puede reprogramar.'
    : editDisabled
      ? 'Guardá o descartá los cambios de la grilla antes de editar un partido.'
      : undefined;

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...dragAttributes}
      className={cn(
        // `select-none` plus the callout reset are for touch: the 250ms press-and-hold that
        // starts a drag on iOS also fires the text-selection callout over the player names,
        // which then fights the drag for the gesture. Deliberately no `touch-action: none`
        // here — see `TOUCH_SENSOR_OPTIONS` in `SharedGridView.tsx` for why that would break
        // page scrolling instead of fixing anything.
        'select-none rounded-md border bg-white p-2 text-xs shadow-sm [-webkit-touch-callout:none]',
        pending && 'border-dashed border-sky-400 bg-sky-50',
        locked && 'opacity-60',
        isDragging && 'opacity-40',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <span
            className={cn(
              'h-2 w-2 shrink-0 rounded-full',
              TOURNAMENT_DOT_COLORS[colorIndex % TOURNAMENT_DOT_COLORS.length],
            )}
            aria-hidden
          />
          <span className="truncate text-gray-500">{tournamentName}</span>
        </div>
        <span className="shrink-0 text-gray-400">{match.stage}</span>
      </div>

      <div className="mt-1 space-y-0.5">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate font-medium text-gray-900">
            {team1Name}
          </span>
          <AvailabilityStatusIcon ok={team1Ok} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="truncate font-medium text-gray-900">
            {team2Name}
          </span>
          <AvailabilityStatusIcon ok={team2Ok} />
        </div>
      </div>

      <div className="mt-1.5 flex items-center gap-3">
        {/* Hidden on mobile: the modal it opens is the only place that shows both pairs'
          availability in detail, and on a phone that section doesn't fit — it gets squeezed into a
          ~130px scroll strip that doesn't even show concrete times. And on a phone the modal offers
          nothing the drag doesn't: the date changes by dragging across columns, and a time outside
          every slot is reached with "Agregar horario" and dropping the card there. */}
        <button
          type="button"
          className={cn(
            'hidden items-center gap-1 sm:flex',
            editBlockedReason
              ? 'cursor-not-allowed text-gray-300'
              : 'text-gray-500 hover:text-gray-900',
          )}
          onClick={() => onEdit(match)}
          disabled={!!editBlockedReason}
          title={editBlockedReason}
        >
          <Pencil className="h-3 w-3" />
          Editar
        </button>
        {/* Not gated by `editBlockedReason`: it only reads, so it stays usable while a draft is
          pending — which is when the organizer most needs to see each pair's times. */}
        <GridAvailabilityPopover
          team1Label={team1Name}
          team2Label={team2Name}
          team1Availability={match.team1Availability}
          team2Availability={match.team2Availability}
          team1Undecided={match.team1.players.length === 0}
          team2Undecided={match.team2.players.length === 0}
          date={date}
        />
      </div>
    </div>
  );
});

/** Draggable wrapper around `GridMatchCardView`, used for every real card in the grid. */
export function GridMatchCard(props: GridMatchCardProps) {
  const locked = props.match.hasResult;

  // Matches with a loaded result can't be dragged: they show, but their schedule is final.
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
    id: props.match.matchId,
    data: { match: props.match },
    disabled: locked,
  });

  // The card is also the activator: with no activator node, the keyboard sensor starts a drag on
  // Enter/Space from any element inside the card, so "Editar" and "Horarios" couldn't be pressed
  // from the keyboard. With it, only the focused card itself starts a keyboard drag.
  const setCardRef = useCallback(
    (node: HTMLElement | null) => {
      setNodeRef(node);
      setActivatorNodeRef(node);
    },
    [setNodeRef, setActivatorNodeRef],
  );

  return (
    <GridMatchCardView
      {...props}
      setNodeRef={setCardRef}
      dragListeners={listeners}
      dragAttributes={attributes}
      isDragging={isDragging}
    />
  );
}
